import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Tooltip,
  useTheme,
  CircularProgress,
  Avatar
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachMoney as MoneyIcon,
  CreditCard as CreditCardIcon,
  Payment as PaymentIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/es';
import PendingIcon from '@mui/icons-material/Pending';
import PriceCheckIcon from '@mui/icons-material/PriceCheck';
import MoneyOffCsredIcon from '@mui/icons-material/MoneyOffCsred';
import PagosPorSucursal from './PagosPorSucursal';

moment.locale('es');

const formatNumber = (value) => {
  if (!value && value !== 0) return '';
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(numValue)) return '';
  
  const [entero, decimal] = numValue.toFixed(2).split('.');
  const enteroFormateado = entero.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  
  return `${enteroFormateado},${decimal}`;
};

const AlmanaqueAcreditaciones = ({ conciliacion, loading = false }) => {
  const theme = useTheme();
  const [expandedDates, setExpandedDates] = useState({});

  // Procesar los datos para agrupar por fecha de acreditación
  const datosAgrupados = useMemo(() => {
    if (!conciliacion) return {};

    // Extraer todos los pagos con money_release_date
    const todosLosPagos = [];
    
    // De coincidencias
    if (conciliacion.coincidencias) {
      conciliacion.coincidencias.forEach(item => {
        if (item.mp?.money_release_date) {
          todosLosPagos.push({
            ...item.mp,
            dbData: item.db,
            tipo: 'coincidencia',
            coincide: item.coincide,
            diferencia: item.diferencia
          });
        }
      });
    }

    // De soloMP
    if (conciliacion.soloMP) {
      conciliacion.soloMP.forEach(item => {
        if (item.money_release_date) {
          todosLosPagos.push({
            ...item,
            tipo: 'soloMP'
          });
        }
      });
    }

    // Agrupar por fecha
    const agrupado = {};
    todosLosPagos.forEach(pago => {
      const fecha = moment(pago.money_release_date).format('YYYY-MM-DD');
      if (!agrupado[fecha]) {
        agrupado[fecha] = {
          fecha: pago.money_release_date,
          pagos: [],
          total: 0,
          cantidad: 0,
          pendientes: 0, 
          acreditados: 0 
        };
      }
      agrupado[fecha].pagos.push(pago);
      agrupado[fecha].total += pago.transaction_amount || 0;
      agrupado[fecha].cantidad += 1;
      
      // Contar pendientes y acreditados
      const status = pago.money_release_status?.toLowerCase?.() || '';
      if (status === 'pending') {
        agrupado[fecha].pendientes += 1;
      } else if (status === 'released') {
        agrupado[fecha].acreditados += 1;
      }
    });

    // Ordenar fechas 
    const fechasOrdenadas = Object.keys(agrupado).sort((a, b) => a.localeCompare(b));
    const resultado = {};
    fechasOrdenadas.forEach(fecha => {
      resultado[fecha] = agrupado[fecha];
    });

    return resultado;
  }, [conciliacion]);

  const toggleExpand = (fecha) => {
    setExpandedDates(prev => ({
      ...prev,
      [fecha]: !prev[fecha]
    }));
  };

  const getMetodoPagoLabel = (paymentMethod) => {
    const metodos = {
      // Tarjetas de crédito
      'visa': 'Visa',
      'master': 'Mastercard',
      'amex': 'American Express',
      'cabal': 'Cabal',
      'naranja': 'Naranja',
      'argencard': 'Argencard',
      'diners': 'Diners',
      
      // Tarjetas de débito
      'debvisa': 'Visa Débito',
      'debmaster': 'Mastercard Débito',
      'debcabal': 'Cabal Débito',
      'maestro': 'Maestro',
      
      // Tarjetas prepago
      'visa_prepago': 'Visa Prepago',
      'master_prepago': 'Mastercard Prepago',
      
      // Efectivo / Tickets
      'pagofacil': 'Pago Fácil',
      'rapipago': 'Rapipago',
      
      // Transferencias / Otros
      'account_money': 'Saldo Mercado Pago',
      'interop_transfer': 'Transferencia Bancaria Interoperable',
      'debin': 'DEBIN',
      'transferencia': 'Transferencia Bancaria',
      'pix': 'Pix',
      'paypal': 'PayPal'
    };
    
    if (metodos[paymentMethod]) {
      return metodos[paymentMethod];
    }
    
    // Buscar por coincidencia parcial
    const metodoEncontrado = Object.keys(metodos).find(key => 
      paymentMethod?.toLowerCase().includes(key.toLowerCase()) || 
      key.toLowerCase().includes(paymentMethod?.toLowerCase())
    );
    
    if (metodoEncontrado) {
      return metodos[metodoEncontrado];
    }
    
    return paymentMethod || 'Desconocido';
  };

  const getMetodoPagoIcon = (paymentMethod) => {
    // Saldo de Mercado Pago
    if (paymentMethod === 'account_money') {
      return <AccountBalanceIcon sx={{ fontSize: 16, color: '#1976d2' }} />;
    }
    
    // Transferencias interoperables
    if (paymentMethod === 'interop_transfer') {
      return <AccountBalanceIcon sx={{ fontSize: 16, color: '#7b1fa2' }} />;
    }
    
    // Tarjetas de crédito
    const creditCards = ['visa', 'master', 'amex', 'cabal', 'naranja', 'argencard', 'diners'];
    if (creditCards.includes(paymentMethod)) {
      return <CreditCardIcon sx={{ fontSize: 16, color: '#4caf50' }} />;
    }
    
    // Tarjetas de débito
    const debitCards = ['debvisa', 'debmaster', 'debcabal', 'maestro'];
    if (debitCards.includes(paymentMethod)) {
      return <CreditCardIcon sx={{ fontSize: 16, color: '#ff9800' }} />;
    }
    
    // Tickets / Efectivo
    const tickets = ['pagofacil', 'rapipago'];
    if (tickets.includes(paymentMethod)) {
      return <PaymentIcon sx={{ fontSize: 16, color: '#9c27b0' }} />;
    }
    
    // Default
    return <CreditCardIcon sx={{ fontSize: 16, color: '#757575' }} />;
  };

  const getTipoPagoBadge = (paymentMethod) => {
    const creditCards = ['visa', 'master', 'amex', 'cabal', 'naranja', 'argencard', 'diners'];
    const debitCards = ['debvisa', 'debmaster', 'debcabal', 'maestro'];
    const tickets = ['pagofacil', 'rapipago'];
    const transferencias = ['interop_transfer', 'debin', 'transferencia', 'pix'];
    
    if (paymentMethod === 'account_money') {
      return <Chip label="Saldo MP" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e3f2fd', color: '#1976d2' }} />;
    }
    if (transferencias.includes(paymentMethod)) {
      return <Chip label="Transferencia" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#f3e5f5', color: '#7b1fa2' }} />;
    }
    if (creditCards.includes(paymentMethod)) {
      return <Chip label="Crédito" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8f5e9', color: '#2e7d32' }} />;
    }
    if (debitCards.includes(paymentMethod)) {
      return <Chip label="Débito" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fff3e0', color: '#ed6c02' }} />;
    }
    if (tickets.includes(paymentMethod)) {
      return <Chip label="Efectivo" size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#fce4ec', color: '#c62828' }} />;
    }
    return null;
  };

  const getEstadoChip = (tipo, coincide) => {
    if (tipo === 'coincidencia') {
      if (coincide) {
        return {
          label: 'Coincide',
          color: 'success',
          icon: <CheckCircleIcon sx={{ fontSize: 14 }} />
        };
      } else {
        return {
          label: 'Difiere',
          color: 'warning',
          icon: <WarningIcon sx={{ fontSize: 14 }} />
        };
      }
    } else {
      return {
        label: 'Solo MP',
        color: 'info',
        icon: <InfoIcon sx={{ fontSize: 14 }} />
      };
    }
  };

  // Función corregida para el estado de acreditación
  const getEstadoAcreditacion = (moneyReleaseStatus) => {
    const status = moneyReleaseStatus?.toLowerCase?.() || '';
    
    if (status === 'released') {
      return {
        label: 'DINERO ACREDITADO',
        color: 'success',
        icon: <PriceCheckIcon sx={{ fontSize: 14 }} />
      };
    } else if (status === 'pending') {
      return {
        label: 'DINERO PENDIENTE',
        color: 'warning',
        icon: <MoneyOffCsredIcon sx={{ fontSize: 14 }} />
      };
    } else {
      return {
        label: 'DESCONOCIDO',
        color: 'default',
        icon: <InfoIcon sx={{ fontSize: 14 }} />
      };
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress size={40} sx={{ color: '#757575' }} />
      </Box>
    );
  }

  const fechas = Object.keys(datosAgrupados);

  if (fechas.length === 0) {
    return (
      <Paper sx={{ p: 3, textAlign: 'center', bgcolor: '#fafafa' }}>
        <CalendarIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 1 }} />
        <Typography variant="body1" color="textSecondary">
          No hay fechas de acreditación disponibles
        </Typography>
        <Typography variant="caption" color="textSecondary">
          Los pagos se mostrarán aquí cuando tengan fecha de acreditación
        </Typography>
      </Paper>
    );
  }

  // Calcular estadísticas generales
  const totalPagos = Object.values(datosAgrupados).reduce((acc, dia) => acc + dia.cantidad, 0);
  const totalMonto = Object.values(datosAgrupados).reduce((acc, dia) => acc + dia.total, 0);
  const promedioDiario = totalMonto / fechas.length;
  const totalPendientes = Object.values(datosAgrupados).reduce((acc, dia) => acc + dia.pendientes, 0);
  const totalAcreditados = Object.values(datosAgrupados).reduce((acc, dia) => acc + dia.acreditados, 0);

  return (
    <Box>
      {/* Encabezado con estadísticas */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        mb: 2,
        flexWrap: 'wrap',
        gap: 1
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalendarIcon sx={{ color: '#1976d2', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Calendario de Acreditaciones
          </Typography>
          <Chip 
            label={`${fechas.length} días`} 
            size="small" 
            sx={{ ml: 1, bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 600 }} 
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip
            icon={<PaymentIcon sx={{ fontSize: 16 }} />}
            label={`${totalPagos} pagos`}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} />}
            label={`${totalAcreditados} acreditados`}
            size="small"
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32' }}
          />
          <Chip
            icon={<PendingIcon sx={{ fontSize: 16, color: '#ed6c02' }} />}
            label={`${totalPendientes} pendientes`}
            size="small"
            sx={{ bgcolor: '#fff3e0', color: '#ed6c02' }}
          />
          <Chip
            icon={<MoneyIcon sx={{ fontSize: 16 }} />}
            label={`Total: $${formatNumber(totalMonto)}`}
            size="small"
            sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
          />
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Grid de días */}
      <Grid container spacing={2} mb={10}>
        {fechas.map((fechaKey) => {
          const dia = datosAgrupados[fechaKey];
          const isExpanded = expandedDates[fechaKey] || false;
          const fechaFormateada = moment(dia.fecha).format('dddd, DD [de] MMMM [de] YYYY');
          const fechaRelativa = moment(dia.fecha).fromNow();
          const diaSemana = moment(dia.fecha).format('ddd');
          const numeroDia = moment(dia.fecha).format('DD');
          const mes = moment(dia.fecha).format('MMM');

          // Determinar color del día según pendientes
          const tienePendientes = dia.pendientes > 0;
          const badgeColor = tienePendientes ? '#ed6c02' : '#1976d2';

          return (
            <Grid item xs={12} key={fechaKey}>
              <Paper
                elevation={isExpanded ? 3 : 1}
                sx={{
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  border: `2px solid ${isExpanded ? '#1976d2' : '#e8e8e8'}`,
                  '&:hover': {
                    boxShadow: 3,
                    borderColor: isExpanded ? '#1976d2' : '#bdbdbd'
                  }
                }}
              >
                {/* Cabecera del día - clickeable */}
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    bgcolor: isExpanded ? '#f5f9ff' : '#fafafa',
                    transition: 'background-color 0.2s ease',
                    '&:hover': {
                      bgcolor: isExpanded ? '#eef4fc' : '#f0f0f0'
                    }
                  }}
                  onClick={() => toggleExpand(fechaKey)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                    {/* Badge del día */}
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: badgeColor,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        boxShadow: 1,
                        transition: 'background-color 0.3s ease'
                      }}
                    >
                      <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.6rem', opacity: 0.8 }}>
                        {diaSemana.toUpperCase()}
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
                        {numeroDia}
                      </Typography>
                      <Typography variant="caption" sx={{ lineHeight: 1, fontSize: '0.6rem', opacity: 0.8 }}>
                        {mes.toUpperCase()}
                      </Typography>
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {fechaFormateada}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {fechaRelativa} • {dia.cantidad} pago{dia.cantidad !== 1 ? 's' : ''}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {dia.pendientes > 0 && (
                        <Chip
                          icon={<PendingIcon sx={{ fontSize: 16 }} />}
                          label={`${dia.pendientes} pendiente${dia.pendientes !== 1 ? 's' : ''}`}
                          size="small"
                          color="warning"
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      )}
                      {dia.acreditados > 0 && (
                        <Chip
                          icon={<PriceCheckIcon sx={{ fontSize: 16 }} />}
                          label={`${dia.acreditados} acreditado${dia.acreditados !== 1 ? 's' : ''}`}
                          size="small"
                          color="success"
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          Total a acreditar
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
                          ${formatNumber(dia.total)}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small"
                        sx={{ 
                          bgcolor: isExpanded ? '#1976d2' : 'transparent',
                          color: isExpanded ? 'white' : '#757575',
                          '&:hover': {
                            bgcolor: isExpanded ? '#1565c0' : '#e0e0e0'
                          }
                        }}
                      >
                        {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                  </Box>
                </Box>

<Collapse in={isExpanded} timeout="auto" unmountOnExit>
  <Box sx={{ p: 2, bgcolor: '#fafafa' }}>

    <PagosPorSucursal pagos={dia.pagos} fecha={fechaKey} />
    
    {/* Resumen del día (se mantiene) */}
    <Box sx={{ 
      mt: 2, 
      pt: 1.5, 
      borderTop: '2px solid #e8e8e8',
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      gap: 3
    }}>
      <Box sx={{ display: 'flex', gap: 3 }}>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Total pagos
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center' }}>
            {dia.cantidad}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Pendientes
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center', color: '#ed6c02' }}>
            {dia.pendientes}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Acreditados
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 600, textAlign: 'center', color: '#2e7d32' }}>
            {dia.acreditados}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
            Monto total
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1976d2' }}>
            ${formatNumber(dia.total)}
          </Typography>
        </Box>
      </Box>
    </Box>
  </Box>
</Collapse>
              </Paper>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default AlmanaqueAcreditaciones;